import type { HeroAnimationFacing } from '@/modules/game-domain/render-snapshot';
import type { GameTextureManifestEntry } from '@/game/assets/resolveGameAssetUrl';
import catalogJson from '../../../assets/world-art/animations/meshy-npcs.json';
import legacyJson from '../../../assets/world-art/animations/individual-idles/manifest.json';

export const MESHY_NPC_MODELS = ['bounty-hunter-v2', 'elder-v1', 'guard-hunter-v1', 'healer-v1', 'herald-v3', 'innkeeper-v1', 'merchant-v1', 'road-blacksmith-v1', 'road-boy-v2', 'road-merchant-v2', 'townswoman-v1', 'weapon-smith-v2'] as const;
export type MeshyNpcModel = typeof MESHY_NPC_MODELS[number];
export type MeshyNpcClip = 'breathing' | 'bored' | 'catwalk' | 'looking-around' | 'warrior-idle' | 'walk';
export interface MeshyNpcClipDefinition {
  file: string; width: number; height: number; frames: number; columns: number; rows: number;
  frameDurationsMs: readonly number[]; loop: boolean;
  presentation: NonNullable<GameTextureManifestEntry['presentation']>;
}
interface Catalog { npcs: Record<string, { clips: Record<MeshyNpcClip, Record<HeroAnimationFacing, MeshyNpcClipDefinition>> }> }
const catalog = catalogJson as unknown as Catalog;
const models = new Set<string>(MESHY_NPC_MODELS);
const legacyModels = new Map<string, MeshyNpcModel>();
for (const character of legacyJson.characters) {
  const model = character.file.match(/approved-npcs\/([^/]+)\//)?.[1];
  if (model && models.has(model)) legacyModels.set(character.character, model as MeshyNpcModel);
}

/** Keep the already accepted NPC appearance binding; heroes never enter this loader. */
export function meshyNpcModel(appearanceKey: string): MeshyNpcModel | null {
  if (/^herald_v[123]$/.test(appearanceKey)) return 'herald-v3';
  return models.has(appearanceKey) ? appearanceKey as MeshyNpcModel : legacyModels.get(appearanceKey) ?? null;
}
export function meshyNpcClip(model: MeshyNpcModel, clip: MeshyNpcClip, facing: HeroAnimationFacing): MeshyNpcClipDefinition {
  return catalog.npcs[model]!.clips[clip][facing];
}
export function meshyNpcDuration(definition: MeshyNpcClipDefinition): number {
  return definition.frameDurationsMs.reduce((total, ms) => total + ms, 0);
}
/** Exact held-pose timing, including the short final42ms walk interval. */
export function meshyNpcFrame(definition: MeshyNpcClipDefinition, elapsedMs: number): number {
  const duration = meshyNpcDuration(definition);
  const elapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  let phase = definition.loop ? elapsed % duration : Math.min(elapsed, duration);
  for (let frame = 0; frame < definition.frames - 1; frame++) {
    const held = definition.frameDurationsMs[frame]!;
    if (phase < held) return frame;
    phase -= held;
  }
  return definition.frames - 1;
}
const STOPPED_CLIPS = ['bored', 'catwalk', 'looking-around', 'warrior-idle'] as const;
/** A server-owned stop epoch chooses once; sampling the same stop never rerolls. */
export function stoppedMeshyNpcClip(actorKey: string, stopEpoch: string | number): Exclude<MeshyNpcClip, 'walk' | 'breathing'> {
  let hash = 2166136261;
  for (const ch of `${actorKey}:${stopEpoch}`) hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619);
  return STOPPED_CLIPS[(hash >>> 0) % STOPPED_CLIPS.length]!;
}
export function meshyNpcAnchor(definition: MeshyNpcClipDefinition): { x: number; y: number } {
  return { x: definition.presentation.groundContactPx.x / definition.width,
    y: definition.presentation.groundContactPx.y / definition.height };
}

export const SERVER_NPC_IDLE_CLIPS = ['breathing', 'bored', 'catwalk', 'looking-around', 'warrior-idle'] as const;
